/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from '@web-stories-wp/react';
import { useStoryAnimationContext } from '@web-stories-wp/animation';
import styled from 'styled-components';

const PlayerContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const getLoopValue = ({ loop, loopCount }) => {
  if (!loop) {
    return false;
  }

  return loopCount ?? true;
};

// The supported entrance-only straps rest on their final playable frame.
// Lottie's frame numbers are relative to the in point; the out point is exclusive.
const getInspectionFrame = (animation) =>
  Math.max(0, (animation?.totalFrames || 1) - 1);

const requestFrame = (callback) =>
  typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame(callback)
    : setTimeout(callback, 0);

const cancelFrame = (frameId) => {
  if (typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(frameId);
    return;
  }

  clearTimeout(frameId);
};

const getDebugStore = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!window.__STUDIO_LOTTIE_DEBUG__) {
    window.__STUDIO_LOTTIE_DEBUG__ = {
      players: {},
      updatedAt: null,
    };
  }

  return window.__STUDIO_LOTTIE_DEBUG__;
};

const summarizeDefinition = (animationData) => ({
  assetCount: Array.isArray(animationData.assets)
    ? animationData.assets.length
    : 0,
  embeddedAssetCount: Array.isArray(animationData.assets)
    ? animationData.assets.filter(
        (asset) => asset?.e || asset?.p?.startsWith?.('data:')
      ).length
    : 0,
  fps: animationData.fr,
  height: animationData.h,
  inPoint: animationData.ip,
  layerCount: Array.isArray(animationData.layers)
    ? animationData.layers.length
    : 0,
  outPoint: animationData.op,
  version: animationData.v,
  width: animationData.w,
});

const getContainerSnapshot = (container) => {
  const svg = container?.querySelector('svg');

  return {
    childElementCount: container?.childElementCount ?? 0,
    hasSvg: Boolean(svg),
    htmlPreview: container?.innerHTML?.slice(0, 300) ?? '',
    svgLength: svg?.outerHTML?.length ?? 0,
  };
};

const updateDebugStore = ({
  animation,
  animationData,
  container,
  elementId,
  event,
  patch = {},
}) => {
  const store = getDebugStore();
  if (!store || !elementId) {
    return;
  }

  const previous = store.players[elementId] || {
    definition: summarizeDefinition(animationData),
    elementId,
    events: [],
  };
  const eventEntry = {
    at: new Date().toISOString(),
    event,
  };
  const events = event
    ? [...previous.events, eventEntry].slice(-40)
    : previous.events;

  store.players[elementId] = {
    ...previous,
    ...getContainerSnapshot(container),
    ...patch,
    animationIsLoaded: animation?.isLoaded ?? previous.animationIsLoaded,
    currentFrame: animation?.currentFrame ?? previous.currentFrame,
    events,
    totalFrames: animation?.totalFrames ?? previous.totalFrames,
    updatedAt: eventEntry.at,
  };
  store.updatedAt = eventEntry.at;
};

function LottieAnimationControls({ adapter, elementId }) {
  const {
    actions: { hoistWAAPIAnimation },
  } = useStoryAnimationContext();

  useEffect(
    () => hoistWAAPIAnimation({ animation: adapter, elementId }),
    [adapter, elementId, hoistWAAPIAnimation]
  );

  return null;
}

LottieAnimationControls.propTypes = {
  adapter: PropTypes.object.isRequired,
  elementId: PropTypes.string.isRequired,
};

function LottiePlayer({
  animationData,
  elementId,
  enableAnimationControls,
  inspectionMode,
  onError,
  onReady,
  playOnReady,
  playback,
}) {
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const playbackRef = useRef(playback);
  const startTimerRef = useRef(null);
  const holdTimerRef = useRef(null);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const [isHidden, setIsHidden] = useState(false);
  const [adapterReady, setAdapterReady] = useState(false);
  const [hasSvg, setHasSvg] = useState(false);
  const [status, setStatus] = useState('idle');
  const animationDataKey = useMemo(
    () => JSON.stringify(animationData),
    [animationData]
  );
  const stableAnimationData = useMemo(() => animationData, [animationDataKey]);

  onReadyRef.current = onReady;
  onErrorRef.current = onError;

  useEffect(() => {
    updateDebugStore({
      animation: animationRef.current,
      animationData: stableAnimationData,
      container: containerRef.current,
      elementId,
      event: `status:${status}`,
      patch: { status },
    });
  }, [elementId, stableAnimationData, status]);

  useEffect(() => {
    playbackRef.current = playback;
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    animationRef.current?.setSpeed(playback.speed);
  }, [playback]);

  const adapter = useMemo(() => {
    const clearStartTimer = () => {
      if (startTimerRef.current) {
        clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
    };

    const clearHoldTimer = () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    };

    const reset = () => {
      clearStartTimer();
      clearHoldTimer();
      setIsHidden(false);
      animationRef.current?.stop();
      animationRef.current?.goToAndStop(
        inspectionMode ? getInspectionFrame(animationRef.current) : 0,
        true
      );
    };

    const play = () => {
      clearStartTimer();
      clearHoldTimer();
      const animation = animationRef.current;
      if (!animation || inspectionMode) {
        return;
      }

      setIsHidden(false);
      animation.setSpeed(playbackRef.current.speed);
      animation.goToAndStop(0, true);

      const start = () => animation.play();
      const delayMs = playbackRef.current.startDelay * 1000;
      if (delayMs > 0) {
        startTimerRef.current = setTimeout(start, delayMs);
        return;
      }

      start();
    };

    return {
      get currentTime() {
        return 0;
      },
      set currentTime(value) {
        if (value === 'end' || value <= 0) {
          reset();
        }
      },
      effect: {
        getTiming: () => ({ delay: 0, duration: 0 }),
      },
      cancel: reset,
      pause: () => {
        clearStartTimer();
        animationRef.current?.pause();
      },
      play,
      onfinish: null,
    };
  }, [inspectionMode]);

  useEffect(() => {
    let isMounted = true;
    let animation = null;
    let isReady = false;
    let renderCheckFrameId = null;
    let fallbackRenderTimeoutId = null;
    setAdapterReady(false);
    setHasSvg(false);
    setIsHidden(false);
    setStatus('importing');
    updateDebugStore({
      animationData: stableAnimationData,
      container: containerRef.current,
      elementId,
      event: 'mount',
      patch: { status: 'importing' },
    });

    const handleComplete = () => {
      updateDebugStore({
        animation: animationRef.current,
        animationData: stableAnimationData,
        container: containerRef.current,
        elementId,
        event: 'complete',
        patch: { status },
      });
      const { endBehavior, holdDuration = 0 } = playbackRef.current;
      const finish = () => {
        holdTimerRef.current = null;
        if (!isMounted) {
          return;
        }
        if (endBehavior === 'reset') {
          animationRef.current?.goToAndStop(0, true);
        }
        if (endBehavior === 'hide') {
          setIsHidden(true);
        }
        adapter.onfinish?.();
      };

      if (
        holdDuration > 0 &&
        (endBehavior === 'reset' || endBehavior === 'hide')
      ) {
        if (holdTimerRef.current) {
          clearTimeout(holdTimerRef.current);
        }
        holdTimerRef.current = setTimeout(finish, holdDuration * 1000);
        return;
      }

      finish();
    };

    const updateRenderState = (event = 'render-check') => {
      if (!isMounted) {
        return false;
      }

      const hasRenderedSvg = Boolean(
        containerRef.current?.querySelector('svg')
      );
      setHasSvg(hasRenderedSvg);
      updateDebugStore({
        animation: animationRef.current,
        animationData: stableAnimationData,
        container: containerRef.current,
        elementId,
        event,
        patch: {
          hasSvg: hasRenderedSvg,
          status: hasRenderedSvg ? 'ready' : 'waiting-for-svg',
        },
      });

      if (hasRenderedSvg && animationRef.current?.isLoaded) {
        handleReady();
      } else if (!isReady) {
        setStatus('waiting-for-svg');
      }

      return hasRenderedSvg;
    };

    const scheduleRenderStateUpdate = () => {
      if (renderCheckFrameId !== null) {
        return;
      }

      renderCheckFrameId = requestFrame(() => {
        renderCheckFrameId = null;
        updateRenderState();
      });
    };

    const handleReady = () => {
      if (!isMounted || isReady) {
        return;
      }

      isReady = true;
      animationRef.current?.setSpeed(playbackRef.current.speed);
      animationRef.current?.goToAndStop(
        inspectionMode ? getInspectionFrame(animationRef.current) : 0,
        true
      );
      animationRef.current?.resize?.();
      setHasSvg(true);
      setStatus('ready');
      updateDebugStore({
        animation: animationRef.current,
        animationData: stableAnimationData,
        container: containerRef.current,
        elementId,
        event: 'ready',
        patch: { hasSvg: true, status: 'ready' },
      });
      setAdapterReady(true);
      onReadyRef.current?.();

      if (
        !inspectionMode &&
        (playOnReady ||
          (!enableAnimationControls && playbackRef.current.autoplay))
      ) {
        adapter.play();
      }
    };

    const handleLoadError = (event) => {
      if (!isMounted) {
        return;
      }

      setStatus('error');
      updateDebugStore({
        animation: animationRef.current,
        animationData: stableAnimationData,
        container: containerRef.current,
        elementId,
        event: event?.type || 'load-error',
        patch: {
          errorMessage: event?.nativeError?.message,
          hasSvg: Boolean(containerRef.current?.querySelector('svg')),
          status: 'error',
        },
      });
      onErrorRef.current?.();
    };

    const handleDomLoaded = () => updateRenderState('DOMLoaded');
    const handleDrawnFrame = () => updateRenderState('drawnFrame');

    import('lottie-web')
      .then((lottieModule) => {
        if (!isMounted || !containerRef.current) {
          return;
        }

        setStatus('loading');
        updateDebugStore({
          animationData: stableAnimationData,
          container: containerRef.current,
          elementId,
          event: 'imported',
          patch: { status: 'loading' },
        });
        const lottie = lottieModule.default || lottieModule;
        animation = lottie.loadAnimation({
          animationData: stableAnimationData,
          autoplay: false,
          container: containerRef.current,
          loop: getLoopValue(playbackRef.current),
          renderer: 'svg',
          rendererSettings: {
            progressiveLoad: false,
            preserveAspectRatio: 'xMidYMid meet',
          },
        });
        animationRef.current = animation;
        animation.addEventListener('complete', handleComplete);
        animation.addEventListener('DOMLoaded', handleDomLoaded);
        animation.addEventListener('drawnFrame', handleDrawnFrame);
        animation.addEventListener('data_failed', handleLoadError);
        animation.addEventListener('configError', handleLoadError);
        animation.addEventListener('renderFrameError', handleLoadError);
        setStatus('waiting-for-svg');
        updateDebugStore({
          animation,
          animationData: stableAnimationData,
          container: containerRef.current,
          elementId,
          event: 'loadAnimation',
          patch: { status: 'waiting-for-svg' },
        });

        if (animation.isLoaded) {
          updateRenderState('already-loaded');
        }
        fallbackRenderTimeoutId = setTimeout(() => {
          if (!isMounted || isReady) {
            return;
          }

          animation?.goToAndStop(0, true);
          animation?.resize?.();
          scheduleRenderStateUpdate();
        }, 0);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setStatus('error');
        updateDebugStore({
          animationData: stableAnimationData,
          container: containerRef.current,
          elementId,
          event: 'import-error',
          patch: { status: 'error' },
        });
        onErrorRef.current?.();
      });

    return () => {
      isMounted = false;
      if (startTimerRef.current) {
        clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      if (renderCheckFrameId !== null) {
        cancelFrame(renderCheckFrameId);
      }
      if (fallbackRenderTimeoutId !== null) {
        clearTimeout(fallbackRenderTimeoutId);
      }
      animation?.removeEventListener('complete', handleComplete);
      animation?.removeEventListener('DOMLoaded', handleDomLoaded);
      animation?.removeEventListener('drawnFrame', handleDrawnFrame);
      animation?.removeEventListener('data_failed', handleLoadError);
      animation?.removeEventListener('configError', handleLoadError);
      animation?.removeEventListener('renderFrameError', handleLoadError);
      updateDebugStore({
        animation,
        animationData: stableAnimationData,
        container: containerRef.current,
        elementId,
        event: 'unmount',
        patch: { status: 'unmounted' },
      });
      animation?.destroy();
      if (animationRef.current === animation) {
        animationRef.current = null;
      }
    };
  }, [
    adapter,
    enableAnimationControls,
    inspectionMode,
    playOnReady,
    playback.autoplay,
    playback.loop,
    playback.loopCount,
    stableAnimationData,
  ]);

  return (
    <>
      <PlayerContainer
        key="player"
        ref={containerRef}
        aria-hidden
        data-lottie-has-svg={hasSvg ? 'true' : 'false'}
        data-lottie-status={status}
        data-testid="lottie-player"
        isHidden={isHidden}
      />
      {enableAnimationControls && !inspectionMode && adapterReady && (
        <LottieAnimationControls
          key="controls"
          adapter={adapter}
          elementId={elementId}
        />
      )}
    </>
  );
}

LottiePlayer.propTypes = {
  animationData: PropTypes.object.isRequired,
  elementId: PropTypes.string.isRequired,
  enableAnimationControls: PropTypes.bool,
  inspectionMode: PropTypes.bool,
  onError: PropTypes.func,
  onReady: PropTypes.func,
  playOnReady: PropTypes.bool,
  playback: PropTypes.shape({
    autoplay: PropTypes.bool.isRequired,
    loop: PropTypes.bool.isRequired,
    loopCount: PropTypes.number,
    startDelay: PropTypes.number.isRequired,
    holdDuration: PropTypes.number,
    speed: PropTypes.number.isRequired,
    endBehavior: PropTypes.oneOf(['freeze', 'reset', 'hide']).isRequired,
  }).isRequired,
};

export default LottiePlayer;
